import type { Router } from 'vue-router';

import { stringifyQuery } from 'vue-router';

import { preferences } from '@vben/preferences';
import { useAccessStore, useUserStore } from '@vben/stores';
import { startProgress, stopProgress } from '@vben/utils';

import { LOGIN_PATH } from '#/config';
import { accessRoutes, coreRouteNames } from '#/router/routes';
import { useAuthStore } from '#/store';
import {
  currentHostSubdomainPrefix,
  useDistributorBrandStore,
} from '#/store/brand'; // AI-Generated by Claude · 分销商多域名登录
import {
  distributorHomePath,
  isDistributorFinanceRoute,
  isStoredDistributorLogin,
  resolveFinanceRouteIdentity,
} from '#/utils/distributor-finance-route';

import { generateAccess } from './access';

// 未登录落地页：分销商域名 → 直接登录页（分销商品牌）；总平台 → 速蜂官网首页 LOGIN_PATH // AI-Generated by Claude · 分销商多域名登录 T7
function unauthLandingPath() {
  return currentHostSubdomainPrefix() ? '/auth/login' : LOGIN_PATH;
}

// function isHomeUrl() {
//   const location = useBrowserLocation();
//   const { host } = location.value;

//   return host === import.meta.env.VITE_HOME_URL;
// }

/**
 * 通用守卫配置
 * @param router
 */
function setupCommonGuard(router: Router) {
  // 记录已经加载的页面
  const loadedPaths = new Set<string>();

  router.beforeEach(async (to) => {
    to.meta.loaded = loadedPaths.has(to.path);

    // 页面加载进度条
    if (!to.meta.loaded && preferences.transition.progress) {
      startProgress();
    }

    return true;
  });

  router.afterEach((to) => {
    // 记录页面是否加载,如果已经加载，后续的页面切换动画等效果不在重复执行

    loadedPaths.add(to.path);

    // 关闭页面加载进度条
    if (preferences.transition.progress) {
      stopProgress();
    }
  });
}

/**
 * 权限访问守卫配置
 * @param router
 */
function setupAccessGuard(router: Router) {
  router.beforeEach(async (to, from) => {
    const accessStore = useAccessStore();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    // 分销商域名：确保品牌已加载（含页签标题更新），loaded 有缓存只真正执行一次 // AI-Generated by Claude · 分销商多域名登录
    if (currentHostSubdomainPrefix()) {
      useDistributorBrandStore().loadByCurrentHost();
    }
    // if (isHomeUrl()) {
    //   return true;
    // }
    // 授权回调
    if (to.name === 'AuthCallback') {
      return true;
    }

    // 基本路由，这些路由不需要进入权限拦截
    if (coreRouteNames.includes(to.name as string)) {
      if (to.path === LOGIN_PATH && accessStore.accessToken) {
        return decodeURIComponent(
          (to.query?.redirect as string) ||
            userStore.userInfo?.homePath ||
            preferences.app.defaultHomePath,
        );
      }
      return true;
    }

    // accessToken 检查
    if (!accessStore.accessToken) {
      // 明确声明忽略权限访问权限，则可以访问
      if (to.meta.ignoreAccess) {
        return true;
      }

      // 没有访问权限，跳转登录页面（分销商域名→/auth/login，总平台→/home） // AI-Generated by Claude · 分销商多域名登录 T7
      const landingPath = unauthLandingPath();
      if (to.path !== landingPath) {
        return {
          path: landingPath,
          // 如不需要，直接删除 query
          query:
            to.path === preferences.app.defaultHomePath
              ? { ...to.query }
              : { redirect: encodeURIComponent(to.path), ...to.query },
          // 携带当前跳转的页面，登录后重新跳转该页面
          replace: true,
        };
      }
      return to;
    }

    const isDistributorSession =
      isStoredDistributorLogin() ||
      userStore.userInfo?.roles?.includes('distributor');
    if (
      isDistributorSession &&
      !isDistributorFinanceRoute(to.path) &&
      to.path !== distributorHomePath
    ) {
      return {
        path: distributorHomePath,
        replace: true,
      };
    }

    const targetIdentity = resolveFinanceRouteIdentity(to.path);

    if (
      accessStore.isAccessChecked &&
      targetIdentity === 'distributor' &&
      !userStore.userInfo?.roles?.includes('distributor')
    ) {
      userStore.setUserInfo(null);
      accessStore.setIsAccessChecked(false);
    }

    // 是否已经生成过动态路由
    if (accessStore.isAccessChecked) {
      return true;
    }

    // 生成路由表
    // 当前登录用户拥有的角色标识列表
    const userInfo =
      userStore.userInfo ||
      (await authStore.fetchUserInfo(true, targetIdentity));
    const userRoles = userInfo.roles ?? [];
    addPermission(userInfo, userRoles);
    // 生成菜单和路由
    const { accessibleMenus, accessibleRoutes } = await generateAccess({
      roles: userRoles,
      router,
      // 则会在菜单中显示，但是访问会被重定向到403
      routes: accessRoutes,
    });

    // 保存菜单信息和路由信息
    accessStore.setAccessMenus(accessibleMenus);
    accessStore.setAccessRoutes(accessibleRoutes);
    accessStore.setIsAccessChecked(true);

    let redirectPath = (from.query.redirect ??
      (to.path === preferences.app.defaultHomePath
        ? userInfo.homePath || preferences.app.defaultHomePath
        : to.fullPath)) as string;
    // 参数待会
    if (from.query) {
      const query = Object.assign({}, from.query);
      delete query.redirect;
      redirectPath = `${redirectPath}?${stringifyQuery(query)}`;
    }
    return {
      ...router.resolve(decodeURIComponent(redirectPath)),
      replace: true,
    };
  });
}

// 动态设置服务端返回的权限
function addPermission(userInfo: object, userRoles: string[]) {
  if ((userInfo as { showMerchantPlugin?: boolean }).showMerchantPlugin) {
    userRoles.push('showMerchantPlugin');
  }
}

/**
 * 项目守卫配置
 * @param router
 */
function createRouterGuard(router: Router) {
  /** 通用 */
  setupCommonGuard(router);
  /** 权限访问 */
  setupAccessGuard(router);
}

export { createRouterGuard };
